/**
 * Share dialog for BL Advisors and admins.
 *
 * Recipients render as bubbles: the x revokes that share, which cuts the
 * recipient's access on their very next request — the link they hold stops
 * working immediately rather than at some later expiry.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle, Check, Copy, Loader2, Mail, RotateCw, Send, Share2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Loader from "@/components/ui/loader";
import { useToast } from "@/components/ui/toast";
import { api, ApiError } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import type { ListingShare, ListingShareCreateResult } from "@/types";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const STATUS_STYLES: Record<string, string> = {
  active: "bg-emerald-50 border-emerald-200 text-emerald-800",
  revoked: "bg-slate-100 border-slate-200 text-slate-500 line-through",
  expired: "bg-amber-50 border-amber-200 text-amber-800",
};

export default function ShareListingModal({
  listingId,
  listingTitle,
  onClose,
  onChanged,
}: {
  listingId: string;
  listingTitle: string;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const toast = useToast();
  const [shares, setShares] = useState<ListingShare[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [blockedNotice, setBlockedNotice] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    api
      .get<ListingShare[]>(`/listing-shares/listing/${listingId}`)
      .then(setShares)
      .catch(() => toast.error("Couldn't load the recipients for this listing."))
      .finally(() => setLoading(false));
  }, [listingId, toast]);

  useEffect(() => load(), [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function share(e: React.FormEvent) {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!EMAIL_RE.test(value)) {
      toast.error("Enter a valid email address.");
      return;
    }
    setSending(true);
    setBlockedNotice(null);
    try {
      const res = await api.post<ListingShareCreateResult>("/listing-shares/", {
        listing_id: listingId,
        recipients: [{ email: value, name: name.trim() || null }],
      });

      // The API validates each recipient individually and reports both halves,
      // so a registered address is reported precisely rather than failing the
      // whole submission.
      if (res.rejected.length > 0) {
        setBlockedNotice(res.rejected[0].message);
      } else {
        toast.success(`Listing shared with ${value}`);
        setEmail("");
        setName("");
        emailRef.current?.focus();
      }
      if (res.created.length > 0) {
        load();
        onChanged?.();
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't share this listing.");
    } finally {
      setSending(false);
    }
  }

  async function revoke(share: ListingShare) {
    setBusyId(share.id);
    try {
      await api.delete(`/listing-shares/${share.id}`);
      toast.success(`Access removed for ${share.recipient_email}`);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't remove this recipient.");
    } finally {
      setBusyId(null);
    }
  }

  async function resend(share: ListingShare) {
    setBusyId(share.id);
    try {
      await api.post<ListingShare>(`/listing-shares/${share.id}/resend`, {});
      toast.success(`Link re-sent to ${share.recipient_email}`);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't resend this link.");
    } finally {
      setBusyId(null);
    }
  }

  async function copy(share: ListingShare) {
    try {
      await navigator.clipboard.writeText(share.share_url);
      setCopiedId(share.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error("Could not copy the link.");
    }
  }

  const active = shares.filter((s) => s.status === "active");

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-border/80 bg-card shadow-glow"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Share listing"
      >
        <div className="flex items-start justify-between gap-4 border-b border-border/70 p-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">
              <Share2 className="h-3.5 w-3.5" />
              Share listing
            </div>
            <h2 className="mt-1.5 truncate text-lg font-bold text-foreground">{listingTitle}</h2>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={share} className="space-y-3 border-b border-border/70 p-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="share-email" className="mb-1.5 block text-xs font-bold text-foreground">
                Recipient email
              </label>
              <Input
                id="share-email"
                ref={emailRef}
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setBlockedNotice(null);
                }}
                placeholder="name@company.com"
                autoFocus
              />
            </div>
            <div>
              <label htmlFor="share-name" className="mb-1.5 block text-xs font-bold text-foreground">
                Recipient name <span className="font-normal text-muted-foreground">(optional)</span>
              </label>
              <Input
                id="share-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
              />
            </div>
          </div>

          {blockedNotice && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-xs leading-5 text-amber-800">{blockedNotice}</p>
            </div>
          )}

          <Button type="submit" disabled={sending} className="w-full">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? "Sending" : "Send link"}
          </Button>

          <p className="text-xs leading-5 text-muted-foreground">
            The recipient gets a personal link. Opening it emails them a verification code, so the link
            only works from their inbox &mdash; forwarding it won&rsquo;t grant anyone else access.
          </p>
        </form>

        <div className="p-5">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-bold text-foreground">
              Shared with {active.length > 0 && <span className="text-muted-foreground">({active.length})</span>}
            </h3>
          </div>

          {loading ? (
            <Loader size="sm" className="min-h-[100px]" />
          ) : shares.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border/80 bg-muted/20 px-4 py-6 text-center text-sm text-muted-foreground">
              Not shared with anyone yet.
            </p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {shares.map((s) => (
                <li
                  key={s.id}
                  className={`group flex max-w-full items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${
                    STATUS_STYLES[s.status] || STATUS_STYLES.revoked
                  }`}
                  title={
                    s.status === "active"
                      ? `Shared ${formatDate(s.created_at)} · viewed ${s.view_count}×`
                      : `${s.status} · shared ${formatDate(s.created_at)}`
                  }
                >
                  <Mail className="h-3.5 w-3.5 shrink-0 opacity-60" />
                  <span className="truncate">
                    {s.recipient_name ? `${s.recipient_name} · ` : ""}
                    {s.recipient_email}
                  </span>
                  {s.view_count > 0 && s.status === "active" && (
                    <span className="shrink-0 rounded-full bg-white/70 px-1.5 py-0.5 text-[0.62rem] font-bold">
                      {s.view_count}
                    </span>
                  )}

                  {busyId === s.id ? (
                    <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                  ) : s.status === "active" ? (
                    <>
                      <button
                        onClick={() => copy(s)}
                        className="shrink-0 rounded-full p-0.5 opacity-60 hover:opacity-100"
                        aria-label={`Copy link for ${s.recipient_email}`}
                        title="Copy link"
                      >
                        {copiedId === s.id ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      </button>
                      <button
                        onClick={() => revoke(s)}
                        className="shrink-0 rounded-full p-0.5 opacity-60 hover:bg-black/10 hover:opacity-100"
                        aria-label={`Remove access for ${s.recipient_email}`}
                        title="Remove access — their link stops working immediately"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => resend(s)}
                      className="shrink-0 rounded-full p-0.5 opacity-60 hover:opacity-100"
                      aria-label={`Re-share with ${s.recipient_email}`}
                      title="Share again"
                    >
                      <RotateCw className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
